import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ScanLine, Upload, CheckCircle2, FileSpreadsheet } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import TryScanner from '../components/TryScanner';

const STEPS = [
  { icon: Upload, title: 'Upload your receipts', desc: 'Drag & drop up to 20 photos or PDFs of invoices and receipts at once — or snap them on WhatsApp.' },
  { icon: ScanLine, title: 'AI reads every one', desc: 'MoneyFeed extracts the total, merchant, date, VAT/tax, invoice number and all line items from each image — with the items checked against the subtotal.' },
  { icon: CheckCircle2, title: 'Review & import', desc: 'Quickly check the extracted data, tweak anything, then import them all as categorized transactions.' },
  { icon: FileSpreadsheet, title: 'Export tax-ready reports', desc: 'Every scanned receipt is archived and rolls into your CSV, Excel and Schedule C / VAT exports.' },
];

const FAQS = [
  { q: 'Can I scan multiple invoices at once?', a: 'Yes. In MoneyFeed for Business you can bulk-upload up to 20 receipts or invoices per batch and the AI reads them all, then you review and import in one click.' },
  { q: 'What does the invoice scanner extract?', a: 'The total amount, merchant/vendor, date, spending category and the VAT/GST/sales-tax line (including the tax registration number on ZATCA/FTA e-invoices) — plus the invoice number and every line item (description, quantity, unit price and line total), checked against the subtotal so mismatches are flagged.' },
  { q: 'Are the original receipts stored?', a: 'Yes. Every scanned image is archived and linked to its transaction, giving you a complete CPA audit trail for tax time.' },
  { q: 'Which file types work?', a: 'Photos (JPG, PNG, WebP) and PDF invoices. You can upload from your computer on the web, or send them on WhatsApp.' },
];

export default function InvoiceScanner() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: 'Invoice & Receipt Scanner', url: 'https://moneyfeed.io/invoice-scanner', description: 'Free AI invoice and receipt scanner. Just snap or scan — the AI extracts totals, tax and merchant, and exports your data to Excel, PDF or CSV.' },
    { '@context': 'https://schema.org', '@type': 'SoftwareApplication', name: 'MoneyFeed Invoice & Receipt Scanner', applicationCategory: 'FinanceApplication', operatingSystem: 'Web, iOS, Android', offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }, aggregateRating: { '@type': 'AggregateRating', ratingValue: '4.8', ratingCount: '190' } },
    { '@context': 'https://schema.org', '@type': 'HowTo', name: 'How to scan receipts and export to Excel', step: STEPS.map((s, i) => ({ '@type': 'HowToStep', position: i + 1, name: s.title, text: s.desc })) },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#EFF6FF] to-[#D1FAE5]" data-testid="invoice-scanner-page">
      <Seo
        title="Invoice & Receipt Scanner — Bulk Scan to Excel | MoneyFeed"
        description="Scan invoices and receipts with AI. Bulk-upload up to 20 at once, auto-extract totals, tax and merchant, archive the originals and export to Excel or CSV — free to start."
        path="/invoice-scanner"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pt-14 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#0284C7]/10 rounded-full mb-5">
          <ScanLine className="h-4 w-4 text-[#0284C7]" />
          <span className="text-xs font-bold text-[#0284C7] uppercase tracking-wider">AI Invoice Scanner</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">
          Scan invoices & receipts <span className="text-[#0284C7]">in bulk</span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Just snap or scan — MoneyFeed's AI extracts the amount, merchant, date, tax, invoice number and all line items from every receipt or invoice, then exports your data to Excel, PDF or CSV. Bulk-scan a whole folder and import them as tax-ready transactions.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/auth">
            <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="scanner-get-started">
              Try the scanner free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <Link to="/export-to-excel">
            <Button variant="outline" className="w-full sm:w-auto border-[#064E3B]/30 text-[#064E3B] hover:bg-[#064E3B]/5 font-bold rounded-full px-7 py-6 text-base" data-testid="scanner-export-link">
              Export to Excel
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-2xl mx-auto px-4 sm:px-8 pb-8">
        <TryScanner format="CSV" />
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">How the invoice scanner works</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl shadow-md p-6" data-testid={`scanner-step-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-4">
                <s.icon className="h-5 w-5 text-[#0284C7]" />
              </div>
              <h3 className="font-bold text-[#064E3B] mb-1">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Invoice scanner FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`scanner-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-8"><Link to="/scan-center" className="block bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center hover:shadow-md transition"><p className="text-sm font-semibold text-[#064E3B]">Do this automatically for every receipt — meet MoneyFeed Scan Center</p><p className="text-gray-600 text-sm mt-2">Snap or send receipts on WhatsApp or in the app and MoneyFeed reads, categorizes and files them into clean books.</p><span className="inline-block mt-3 text-sm font-semibold text-[#0D9488]">Explore Scan Center →</span></Link></section>
      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
            <Link to="/invoicing" className="hover:text-[#064E3B]">Invoicing for Freelancers</Link>
          <Link to="/business" className="hover:text-[#064E3B]">For Business</Link>
          <Link to="/export-to-excel" className="hover:text-[#064E3B]">Export to Excel</Link>
          <Link to="/for-accountants" className="hover:text-[#064E3B]">For Accountants</Link>
        </div>
      </footer>
    </div>
  );
}
